import * as React from "react";
import {getTranslation} from "../../utils/translationUtil";
import {DocumentType, documentTypeEnumKeyFromValue} from "../../utils/marketOutlookUtils";
import {useNavigate} from "react-router-dom";
import {navigateTo} from "../../../navigateTo";
import {
    MARKET_OUTLOOK_CAPITAL_MARKET_ASSUMPTIONS_DESC,
    MARKET_OUTLOOK_CAPITAL_MARKET_ASSUMPTIONS_TITLE,
    MARKET_OUTLOOK_MARKET_BACKGROUND_DESC, MARKET_OUTLOOK_MARKET_BACKGROUND_TITLE,
    MARKET_OUTLOOK_MEDIUM_TERM_VIEWS_DESC,
    MARKET_OUTLOOK_MEDIUM_TERM_VIEWS_TITLE,
    MARKET_OUTLOOK_QUARTERLY_INVESTMENT_OUTLOOK_DESC, MARKET_OUTLOOK_QUARTERLY_INVESTMENT_OUTLOOK_TITLE
} from "../../utils/commonConstants";

// Font Awesome icon class mapping for insight cards
const INSIGHT_ICON_CLASSES: Record<string, string> = {
    [DocumentType.MTV]: "fa-light fa-chart-line",
    [DocumentType.CMA]: "fa-light fa-chart-simple",
    [DocumentType.QIO]: "fa-light fa-compass",
    [DocumentType.MB]: "fa-light fa-globe",
};

// SVG Icons for insight cards
const InsightIcon: React.FunctionComponent<{type: DocumentType}> = ({type}) => {
    const iconClass = INSIGHT_ICON_CLASSES[type];
    return iconClass ? <i className={iconClass} data-testid={`home-insights__icon-${type}`}/> : null;
};

// Translation keys and default descriptions for each document type
const INSIGHT_DESCRIPTION_KEYS: Record<DocumentType, {key: string, defaultValue: string}> = {
    [DocumentType.MTV]: {
        key: "home.insights.description-mtv",
        defaultValue: MARKET_OUTLOOK_MEDIUM_TERM_VIEWS_DESC
    },
    [DocumentType.CMA]: {
        key: "home.insights.description-cma",
        defaultValue: MARKET_OUTLOOK_CAPITAL_MARKET_ASSUMPTIONS_DESC
    },
    [DocumentType.QIO]: {
        key: "home.insights.description-qio",
        defaultValue: MARKET_OUTLOOK_QUARTERLY_INVESTMENT_OUTLOOK_DESC
    },
    [DocumentType.MB]: {
        key: "home.insights.description-mb",
        defaultValue: MARKET_OUTLOOK_MARKET_BACKGROUND_DESC
    }
};

export const HomeInsights: React.FunctionComponent = () => {
    const navigate = useNavigate();

    const renderInsightCard = (documentType: DocumentType, titleKey: string, titleDefault: string) => {
        const key = documentTypeEnumKeyFromValue(documentType);
        const descConfig = INSIGHT_DESCRIPTION_KEYS[documentType];
        return (
            <div
                className="home-insights__card"
                onClick={() => navigateTo(`/market-outlooks?document-type=${key}`, navigate)}
                data-testid={`home-insights__link-card-${documentType}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigateTo(`/market-outlooks?document-type=${key}`, navigate);
                    }
                }}
            >
                <div className="home-insights__card-header">
                    <div className={`home-insights__card-icon home-insights__card-icon--${key.toLowerCase()}`}>
                        <InsightIcon type={documentType} />
                    </div>
                    <div className="home-insights__card-title">{getTranslation(titleKey, titleDefault)}</div>
                </div>
                <p className="home-insights__card-description">
                    {getTranslation(descConfig.key, descConfig.defaultValue)}
                </p>
            </div>
        );
    };

    return (
        <div className="home-insights__panel" data-testid="home-insights__container">
            <div className="home-insights__panel-header">
                <div className="home-insights__panel-icon">
                    <i className="fa-light fa-circle-info" />
                </div>
                <h2 className="home-insights__panel-title">
                    {getTranslation("home.insights", "Latest Insights")}
                </h2>
            </div>
            <div className="home-insights__panel-grid">
                {renderInsightCard(DocumentType.MTV, "filters.medium-term-views", MARKET_OUTLOOK_MEDIUM_TERM_VIEWS_TITLE)}
                {renderInsightCard(DocumentType.CMA, "filters.capital-market-assumptions", MARKET_OUTLOOK_CAPITAL_MARKET_ASSUMPTIONS_TITLE)}
                {renderInsightCard(DocumentType.QIO, "filters.quarterly-investment-outlook", MARKET_OUTLOOK_QUARTERLY_INVESTMENT_OUTLOOK_TITLE)}
                {renderInsightCard(DocumentType.MB, "filters.market-background", MARKET_OUTLOOK_MARKET_BACKGROUND_TITLE)}
            </div>
        </div>
    );
};
